
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, Shield, LogOut, User as UserIcon } from 'lucide-react';
import { authService, User } from '../services/authService';
import Logo from './Logo';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(authService.getUser());
  const location = useLocation();
  const navigate = useNavigate();

  const ADMIN_EMAIL = 'planspace@outlook.com';

  useEffect(() => {
    const handleAuthChange = () => {
      setUser(authService.getUser());
    };
    window.addEventListener('authChange', handleAuthChange);
    return () => window.removeEventListener('authChange', handleAuthChange);
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const navLinks = [
    { name: 'Directory', path: '/directory' },
    { name: 'Project Wizard', path: '/wizard' },
    { name: 'Pricing', path: '/join' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center group transition-transform active:scale-95">
            <Logo size={40} showText={true} />
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-[10px] uppercase tracking-widest font-black transition-colors duration-200 flex items-center gap-1.5 ${
                  isActive(link.path) ? 'text-brand-600' : 'text-gray-500 hover:text-brand-600'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {!user && (
              <Link
                to="/login"
                className={`text-[10px] uppercase tracking-widest font-black transition-colors duration-200 flex items-center gap-1.5 ${
                  isActive('/login') ? 'text-brand-600' : 'text-gray-500 hover:text-brand-600'
                }`}
              >
                Consultant Login
              </Link>
            )}
            
            {user?.email === ADMIN_EMAIL && (
              <Link
                to="/admin"
                className={`text-[10px] uppercase tracking-widest font-black transition-colors duration-200 flex items-center gap-1.5 ${
                  isActive('/admin') ? 'text-brand-600' : 'text-red-500 hover:text-red-600'
                }`}
              >
                <Shield size={12} /> Master Admin
              </Link>
            )}
          </nav>

          <div className="hidden md:flex items-center space-x-4">
             {user ? (
               <div className="flex items-center gap-6">
                 <Link to="/dashboard" className="flex items-center gap-2 text-xs font-black text-brand-600 transition uppercase tracking-widest hover:text-brand-900">
                    <div className="w-8 h-8 rounded-full bg-brand-50 flex items-center justify-center text-brand-700">
                       <UserIcon size={14} />
                    </div>
                    {user.name}
                 </Link>
                 <button 
                   onClick={handleLogout}
                   className="text-gray-400 hover:text-red-500 transition p-2"
                   title="Logout"
                 >
                   <LogOut size={18} />
                 </button>
               </div>
             ) : (
               <>
                 <Link to="/join" className="bg-brand-900 hover:bg-black text-white px-6 py-3 rounded-xl text-xs font-black transition shadow-lg uppercase tracking-widest">
                   Claim Listing
                 </Link>
               </>
             )}
          </div>

          <div className="md:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-500 p-2"><Menu size={24} /></button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 animate-fade-in shadow-xl">
          <div className="px-6 py-8 space-y-6">
            {navLinks.map((link) => (
              <Link key={link.name} to={link.path} className="flex items-center gap-3 text-sm font-black text-gray-700 uppercase tracking-widest" onClick={() => setIsMenuOpen(false)}>
                {link.name}
              </Link>
            ))}

            {!user && (
              <Link to="/login" className="flex items-center gap-3 text-sm font-black text-gray-700 uppercase tracking-widest" onClick={() => setIsMenuOpen(false)}>
                Consultant Login
              </Link>
            )}
            
            {user?.email === ADMIN_EMAIL && (
              <Link to="/admin" className="flex items-center gap-3 text-sm font-black text-red-500 uppercase tracking-widest" onClick={() => setIsMenuOpen(false)}>
                <Shield size={18} /> Admin Panel
              </Link>
            )}
            
            <div className="pt-6 border-t border-gray-50 flex flex-col gap-4">
              {user ? (
                <>
                  <Link to="/dashboard" className="flex items-center gap-3 text-sm font-black text-brand-600 uppercase tracking-widest" onClick={() => setIsMenuOpen(false)}>
                    <LayoutDashboard size={18} /> My Dashboard
                  </Link>
                  <button onClick={handleLogout} className="flex items-center gap-3 text-sm font-black text-red-500 uppercase tracking-widest">
                    <LogOut size={18} /> Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/join" className="bg-brand-900 text-white px-6 py-4 rounded-xl text-center text-xs font-black uppercase tracking-widest shadow-lg" onClick={() => setIsMenuOpen(false)}>
                    Professional Signup
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
